
/*
 * MARÓC 1986
 * COMPRAR AHORA - VISTA RÁPIDA Y PRODUCTOS
 *
 * Archivo: maroc-quick-buy.js
 *
 * Compatible con main-product.liquid actualizado.
 *
 * Incluye:
 * - Compatibilidad con Quick Buy de Symmetry.
 * - Selección de variante y cantidad.
 * - Tipo y número de documento obligatorios.
 * - Observaciones opcionales.
 * - Conservación temporal de datos.
 * - Guardado y verificación en Shopify.
 * - Redirección al checkout.
 */

(function () {
  'use strict';

  if (window.__marocQuickBuyReady) return;
  window.__marocQuickBuyReady = true;

  const validTypes = [
    'CC', 'CE', 'NIT',
    'PAS', 'PPT', 'TI'
  ];

  const memory = new Map();

  let current = null;
  let working = false;

  // Construir rutas de Shopify.
  function endpoint(path) {
    const root =
      window.Shopify?.routes?.root || '/';

    return root.replace(/\/?$/, '/') + path;
  }

  // Solicitudes AJAX a Shopify.
  async function jsonRequest(path, options) {
    const response = await fetch(endpoint(path), {
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      ...options
    });

    const result = await response.json()
      .catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        result.description ||
        result.message ||
        'No se pudo actualizar el carrito.'
      );
    }

    return result;
  }

  // Campos de facturación.
  function controls(modal) {
    return {
      type: modal.querySelector(
        '[data-maroc-type]'
      ),
      number: modal.querySelector(
        '[data-maroc-number]'
      ),
      note: modal.querySelector(
        '[data-maroc-note]'
      ),
      error: modal.querySelector(
        '[data-maroc-error]'
      ),
      submit: modal.querySelector(
        '[data-maroc-submit]'
      )
    };
  }

  // Mensajes de error.
  function error(modal, message) {
    const el = controls(modal).error;

    if (!el) return;

    el.textContent = message || '';
    el.hidden = !message;
  }

  // Conservar información temporalmente.
  function readMemory(modal) {
    const c = controls(modal);

    memory.set(modal.id, {
      type: c.type.value,
      number: c.number.value,
      note: c.note.value
    });
  }

  // Recuperar información temporal.
  function applyMemory(modal) {
    const data = memory.get(modal.id);

    if (!data) return;

    const c = controls(modal);

    c.type.value = data.type || '';
    c.number.value = data.number || '';
    c.note.value = data.note || '';
  }

  // Encontrar formulario del producto.
  // Compatible con el contenido AJAX de Quick Buy.
  function productForm(trigger) {
    return trigger.closest('product-form')
      ?.querySelector('form.js-product-form') || null;
  }

  // Obtener variante, cantidad y propiedades.
  function productItem(form) {
    if (!form) {
      throw new Error(
        'No se encontró el formulario del producto.'
      );
    }

    // Symmetry actualiza el ID al seleccionar talla/color.
    const data = new FormData(form);

    const id = String(
      data.get('id') || ''
    ).trim();

    const quantity = Number(
      data.get('quantity') || '1'
    );

    if (!/^\d+$/.test(id)) {
      throw new Error(
        'Selecciona una talla y color disponibles.'
      );
    }

    if (
      !Number.isInteger(quantity) ||
      quantity < 1
    ) {
      throw new Error(
        'Selecciona una cantidad válida.'
      );
    }

    // Comprobar disponibilidad.
    const atc = form.querySelector(
      'button[name="add"].add-to-cart'
    );

    if (atc?.disabled) {
      throw new Error(
        'Esta variante no está disponible.'
      );
    }

    if (!form.checkValidity()) {
      form.reportValidity();

      throw new Error(
        'Revisa las opciones obligatorias del producto.'
      );
    }

    const item = {
      id: Number(id),
      quantity: quantity
    };

    // Conservar personalizaciones.
    const properties = {};

    for (const [key, value] of data.entries()) {
      const match = key.match(
        /^properties\[(.+)\]$/
      );

      if (
        match &&
        typeof value === 'string'
      ) {
        properties[match[1]] = value;
      }
    }

    if (Object.keys(properties).length) {
      item.properties = properties;
    }

    // Conservar planes de venta.
    if (data.get('selling_plan')) {
      item.selling_plan =
        data.get('selling_plan');
    }

    return item;
  }

  // Validar documentos.
  function validate(modal) {
    const c = controls(modal);

    const type = c.type.value.trim();
    const number = c.number.value.trim();

    if (!validTypes.includes(type)) {
      error(
        modal,
        'Selecciona el tipo de documento.'
      );

      c.type.focus();
      return null;
    }

    if (
      !/^[A-Za-z0-9 .-]{3,30}$/.test(number)
    ) {
      error(
        modal,
        'Ingresa un número de documento válido (3 a 30 caracteres).'
      );

      c.number.focus();
      return null;
    }

    error(modal, '');

    return {
      type: type,
      number: number,
      note: c.note.value
    };
  }

  // Abrir ventana emergente.
  async function show(trigger) {
    const modal = trigger.closest('product-form')
      ?.querySelector('[data-maroc-quick-modal]');

    if (!modal || working) return;

    const form = productForm(trigger);

    try {
      productItem(form);
    } catch (e) {
      window.alert(e.message);
      return;
    }

    current = {
      modal: modal,
      trigger: trigger,
      form: form
    };

    // Llevar ventana al body para evitar
    // que quede recortada en Quick Buy.
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    modal.hidden = false;

    document.body.style.overflow = 'hidden';

    applyMemory(modal);

    modal.querySelector(
      '.maroc-quick-panel'
    )?.focus();

    error(modal, '');

    // Recuperar datos existentes.
    try {
      const cart = await jsonRequest('cart.js');

      if (current?.modal !== modal) return;

      const c = controls(modal);
      const saved = memory.get(modal.id);

      if (!saved || !saved.type) {
        c.type.value =
          cart.attributes?.[
            'Tipo de documento'
          ] || '';
      }

      if (!saved || !saved.number) {
        c.number.value =
          cart.attributes?.[
            'Numero de documento'
          ] || '';
      }

      if (!saved || !saved.note) {
        c.note.value = cart.note || '';
      }

    } catch (_) {
      // Se podrán completar manualmente.
    }
  }

  // Cerrar la ventana.
  function close(modal) {
    if (working) return;

    readMemory(modal);

    modal.hidden = true;

    document.body.style.overflow = '';

    current?.trigger?.focus();

    current = null;
  }

  // Recordar datos durante cambios AJAX.
  document.addEventListener('input', function (e) {
    const modal = e.target.closest?.(
      '[data-maroc-quick-modal]'
    );

    if (modal) {
      readMemory(modal);
    }
  });

  document.addEventListener('change', function (e) {
    const modal = e.target.closest?.(
      '[data-maroc-quick-modal]'
    );

    if (modal) {
      readMemory(modal);
    }
  });

  // Abrir y cerrar formulario.
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest?.(
      '[data-maroc-quick-buy]'
    );

    if (trigger) {
      e.preventDefault();
      show(trigger);
      return;
    }

    const closer = e.target.closest?.(
      '[data-maroc-close]'
    );

    if (closer) {
      const modal = closer.closest(
        '[data-maroc-quick-modal]'
      );

      if (modal) close(modal);
    }
  });

  // Cerrar con Escape.
  document.addEventListener('keydown', function (e) {
    if (
      e.key === 'Escape' &&
      current?.modal &&
      !current.modal.hidden
    ) {
      close(current.modal);
    }
  });

  // Finalizar compra rápida.
  document.addEventListener(
    'submit',
    async function (e) {
      const quickForm = e.target.closest?.(
        '[data-maroc-quick-form]'
      );

      if (!quickForm) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      if (working || !current) return;

      const modal = quickForm.closest(
        '[data-maroc-quick-modal]'
      );

      const values = validate(modal);

      if (!values) return;

      let item;

      try {
        item = productItem(current.form);
      } catch (ex) {
        error(modal, ex.message);
        return;
      }

      working = true;

      const c = controls(modal);

      c.submit.disabled = true;
      c.submit.textContent = 'PROCESANDO...';

      try {
        // 1. Leer carrito actual.
        const before = await jsonRequest(
          'cart.js'
        );

        // Mantener atributos existentes.
        const attributes = {
          ...(before.attributes || {}),
          'Tipo de documento': values.type,
          'Numero de documento': values.number
        };

        const updatePayload = {
          attributes: attributes
        };

        // Mantener las observaciones anteriores
        // cuando no se escribe una nueva.
        if (values.note.trim()) {
          updatePayload.note = values.note;
        }

        // 2. Guardar datos de facturación.
        const updated = await jsonRequest(
          'cart/update.js',
          {
            method: 'POST',
            body: JSON.stringify(updatePayload)
          }
        );

        // 3. Confirmar el guardado.
        if (
          updated.attributes?.[
            'Tipo de documento'
          ] !== values.type ||

          updated.attributes?.[
            'Numero de documento'
          ] !== values.number ||

          (
            values.note.trim() &&
            updated.note !== values.note
          )
        ) {
          throw new Error(
            'Shopify no confirmó los datos de facturación y observaciones.'
          );
        }

        // 4. Añadir la variante seleccionada.
        await jsonRequest(
          'cart/add.js',
          {
            method: 'POST',
            body: JSON.stringify({
              items: [item]
            })
          }
        );

        // 5. Continuar al checkout.
        window.location.assign(
          endpoint('checkout')
        );

      } catch (ex) {
        error(
          modal,
          ex.message ||
          'No pudimos continuar. Inténtalo nuevamente.'
        );

        working = false;

        c.submit.disabled = false;
        c.submit.textContent =
          'CONTINUAR CON LA COMPRA';
      }
    },
    true
  );

})();
